import React, { useState, useMemo, useEffect, useCallback } from 'react';

import cx from 'classnames';
import { Icon, Flex, Modal, Button, Loading } from 'design-system-web';
import { useTranslation } from 'react-i18next';
import type { ApolloError } from '@apollo/client';

import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';

import { P } from 'components/typography';

import type { VisitorCustomFieldConfig } from 'graphql-query-contracts';

import styles from './CustomFieldsModal.scss';

type CustomFieldsModalProps = {
  isOpen: boolean;
  customFields?: VisitorCustomFieldConfig[];
  loading?: boolean;
  error?: ApolloError;
  onClose: () => void;
  onAdd?: (fields: VisitorCustomFieldConfig[]) => void;
  alreadyAddedFieldIds?: string[];
  hasFetched?: boolean;
};

const FIELDS_PER_PAGE = 5;
const MAX_VISIBLE_PAGES = 3;
const ELLIPSIS = '...';


const FIELD_TYPE_I18N_KEY_PREFIX =
  'web.admin.content.features.visitor.management.general.customFields.fieldTypeValue.';

const getFieldTypeDisplayLabel = (
  type: string,
  t: (key: string) => string
): string => {
  const key = `${FIELD_TYPE_I18N_KEY_PREFIX}${type.toLowerCase()}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

type SortField = 'fieldName' | 'fieldType';
type SortDirection = 'asc' | 'desc';

export function CustomFieldsModal({
  isOpen,
  customFields = [],
  loading = false,
  error,
  onClose,
  onAdd,
  alreadyAddedFieldIds = [],
  hasFetched = true,
}: CustomFieldsModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState<SortField>('fieldName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(
    new Set()
  );

  const alreadyAddedSet = useMemo(
    () => new Set(alreadyAddedFieldIds),
    [alreadyAddedFieldIds]
  );

  const filteredFields = useMemo(() => {
    let result = customFields;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();

      result = result.filter(
        field =>
          field.fieldName.toLowerCase().includes(query) ||
          getFieldTypeDisplayLabel(field.fieldType, t).toLowerCase().includes(query)
      );
    }

    return [...result].sort((a, b) => {
      const aVal =
        sortField === 'fieldType'
          ? getFieldTypeDisplayLabel(a.fieldType, t).toLowerCase()
          : a[sortField].toLowerCase();
      const bVal =
        sortField === 'fieldType'
          ? getFieldTypeDisplayLabel(b.fieldType, t).toLowerCase()
          : b[sortField].toLowerCase();
      const comparison = aVal.localeCompare(bVal);

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [customFields, searchQuery, sortField, sortDirection, t]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredFields.length / FIELDS_PER_PAGE)
  );
  const safeCurrentPage = Math.min(currentPage, totalPages - 1);

  useEffect(() => {
    setCurrentPage(prev => Math.min(prev, Math.max(0, totalPages - 1)));
  }, [totalPages]);

  const paginatedFields = filteredFields.slice(
    safeCurrentPage * FIELDS_PER_PAGE,
    (safeCurrentPage + 1) * FIELDS_PER_PAGE
  );

  const hasFields = filteredFields.length > 0;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(0);
  };

  const selectableFields = useMemo(
    () => filteredFields.filter(f => !alreadyAddedSet.has(f.id)),
    [filteredFields, alreadyAddedSet]
  );

  const isAllSelectableSelected =
    selectableFields.length > 0 &&
    selectableFields.every(f => selectedFieldIds.has(f.id));

  const handleToggleField = useCallback((id: string) => {
    setSelectedFieldIds(prev => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setSelectedFieldIds(prev => {
      const next = new Set(prev);

      if (selectableFields.every(f => prev.has(f.id))) {
        for (const f of selectableFields) {
          next.delete(f.id);
        }
      } else {
        for (const f of selectableFields) {
          next.add(f.id);
        }
      }

      return next;
    });
  }, [selectableFields]);

  const handleAdd = useCallback(() => {
    if (!onAdd || selectedFieldIds.size === 0) return;

    const selected = customFields.filter(f => selectedFieldIds.has(f.id));

    onAdd(selected);
    setSelectedFieldIds(new Set());
  }, [onAdd, selectedFieldIds, customFields]);

  const renderPageNumbers = () => {
    const pages: React.ReactNode[] = [];

    let startPage = Math.max(
      0,
      safeCurrentPage - Math.floor(MAX_VISIBLE_PAGES / 2)
    );
    const endPage = Math.min(startPage + MAX_VISIBLE_PAGES, totalPages);

    if (endPage - startPage < MAX_VISIBLE_PAGES) {
      startPage = Math.max(0, endPage - MAX_VISIBLE_PAGES);
    }

    if (startPage > 0) {
      pages.push(
        <button
          key="first"
          type="button"
          className={styles.pageButton}
          onClick={() => setCurrentPage(0)}
        >
          {1}
        </button>
      );
      if (startPage > 1) {
        pages.push(
          <span key="ellipsis-start" className={styles.ellipsis}>
            {ELLIPSIS}
          </span>
        );
      }
    }

    for (let i = startPage; i < endPage; i++) {
      pages.push(
        <button
          key={i}
          type="button"
          className={`${styles.pageButton} ${safeCurrentPage === i ? styles.active : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i + 1}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="ellipsis-end" className={styles.ellipsis}>
            {ELLIPSIS}
          </span>
        );
      }
      pages.push(
        <button
          key="last"
          type="button"
          className={styles.pageButton}
          onClick={() => setCurrentPage(totalPages - 1)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId="customFieldsModal"
      className={styles.modal}
      size="small"
      includeHeader={false}
      disableContentPadding
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          data-test="customFieldsModalClose"
          aria-label={t(
            'web.admin.content.features.visitor.management.general.customFields.close'
          )}
        >
          <span className={styles.redDot} />
        </button>
      </div>

      <div className={styles.body}>
        <span className={styles.bodyTitle}>
          {t(
            'web.admin.content.features.visitor.management.general.customFields.title'
          )}
        </span>

        {(!hasFetched || loading) && (
          <div className={styles.loadingContainer}>
            <Loading />
          </div>
        )}

        {hasFetched && !loading && error && (
          <div
            className={styles.errorContainer}
            data-test="customFieldsError"
          >
            <Icon
              name="circle-exclamation"
              set={ICON_SET_FONTAWESOME}
              size="large"
            />
            <P>
              {t(
                'web.admin.content.features.visitor.management.general.customFields.loadError'
              )}
            </P>
          </div>
        )}

        {hasFetched && !loading && !error && (
          <>
            <div className={styles.searchAndHeaderBox}>
              <div className={styles.searchInputWrapper}>
                <Icon
                  name="search"
                  set={ICON_SET_FONTAWESOME}
                  className={styles.searchIcon}
                />
                <input
                  type="text"
                  className={styles.searchInput}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t(
                    'web.admin.content.features.visitor.management.general.customFields.searchPlaceholder'
                  )}
                  data-test="customFieldsSearchInput"
                />
              </div>
            </div>

            <div
              className={styles.fieldsContainer}
              data-test="customFieldsListContainer"
            >
              {hasFields ? (
                <table className={styles.fieldsTable}>
                  <thead>
                    <tr className={styles.tableHeader}>
                      <th className={styles.checkboxCell}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={isAllSelectableSelected}
                          onChange={handleToggleAll}
                          disabled={selectableFields.length === 0}
                          aria-label="Select all fields"
                          data-test="customFieldsSelectAll"
                        />
                      </th>
                      <th>
                        <button
                          type="button"
                          className={styles.sortButton}
                          onClick={() => handleSort('fieldName')}
                          data-test="customFieldsSortName"
                        >
                          {t(
                            'web.admin.content.features.visitor.management.general.customFields.fieldName'
                          )}
                          <Icon
                            name="sort"
                            set={ICON_SET_FONTAWESOME}
                            className={styles.sortIcon}
                          />
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className={styles.sortButton}
                          onClick={() => handleSort('fieldType')}
                          data-test="customFieldsSortType"
                        >
                          {t(
                            'web.admin.content.features.visitor.management.general.customFields.fieldType'
                          )}
                          <Icon
                            name="sort"
                            set={ICON_SET_FONTAWESOME}
                            className={styles.sortIcon}
                          />
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedFields.map(field => {
                      const isAdded = alreadyAddedSet.has(field.id);

                      return (
                        <tr
                          key={field.id}
                          className={cx(styles.tableRow, isAdded && styles.tableRowDisabled)}
                          data-test="customFieldItem"
                        >
                          <td className={styles.checkboxCell}>
                            <input
                              type="checkbox"
                              className={styles.checkbox}
                              checked={selectedFieldIds.has(field.id)}
                              onChange={() => handleToggleField(field.id)}
                              disabled={isAdded}
                              aria-label={t(
                                'web.admin.content.features.visitor.management.general.customFields.selectField',
                                { fieldName: field.fieldName }
                              )}
                            />
                          </td>
                          <td className={styles.fieldNameCell}>
                            {field.fieldName}
                          </td>
                          <td className={styles.fieldTypeCell}>
                            {getFieldTypeDisplayLabel(field.fieldType, t)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  className={styles.emptyState}
                  data-test="customFieldsEmptyState"
                >
                  <Icon
                    name="list"
                    set={ICON_SET_FONTAWESOME}
                    size="large"
                    className={styles.emptyStateIcon}
                  />
                  <P className={styles.emptyStateText}>
                    {customFields.length === 0
                      ? t(
                          'web.admin.content.features.visitor.management.general.customFields.emptyState'
                        )
                      : t(
                          'web.admin.content.features.visitor.management.general.customFields.noSearchResults'
                        )}
                  </P>
                </Flex>
              )}
            </div>

            {totalPages > 1 && (
              <Flex
                align="center"
                justify="center"
                gap={2}
                className={styles.pagination}
                data-test="customFieldsPagination"
              >
                <button
                  type="button"
                  className={styles.navButton}
                  onClick={() => setCurrentPage(0)}
                  disabled={safeCurrentPage === 0}
                  aria-label={t('web.admin.content.features.visitor.management.general.customFields.firstPage')}
                >
                  <Icon
                    name="chevron-double-left"
                    set={ICON_SET_FONTAWESOME}
                    size="medium"
                  />
                </button>
                <button
                  type="button"
                  className={styles.navButton}
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={safeCurrentPage === 0}
                  data-test="customFieldsPaginationPrev"
                  aria-label={t('web.admin.content.features.visitor.management.general.customFields.previousPage')}
                >
                  <Icon
                    name="chevron-left"
                    set={ICON_SET_FONTAWESOME}
                    size="medium"
                  />
                </button>

                {renderPageNumbers()}

                <button
                  type="button"
                  className={styles.navButton}
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))
                  }
                  disabled={safeCurrentPage === totalPages - 1}
                  data-test="customFieldsPaginationNext"
                  aria-label={t('web.admin.content.features.visitor.management.general.customFields.nextPage')}
                >
                  <Icon
                    name="chevron-right"
                    set={ICON_SET_FONTAWESOME}
                    size="medium"
                  />
                </button>
                <button
                  type="button"
                  className={styles.navButton}
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={safeCurrentPage === totalPages - 1}
                  aria-label={t('web.admin.content.features.visitor.management.general.customFields.lastPage')}
                >
                  <Icon
                    name="chevron-double-right"
                    set={ICON_SET_FONTAWESOME}
                    size="medium"
                  />
                </button>
              </Flex>
            )}
          </>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          variant="secondary"
          onClick={onClose}
          testId="customFieldsModalCancel"
          className={styles.actionButton}
        >
          {t(
            'web.admin.content.features.visitor.management.general.customFields.cancel'
          )}
        </Button>
        <Button
          variant="primary"
          disabled={selectedFieldIds.size === 0}
          onClick={handleAdd}
          testId="customFieldsModalAdd"
          className={styles.actionButton}
        >
          {t(
            'web.admin.content.features.visitor.management.general.customFields.add'
          )}
        </Button>
      </div>
    </Modal>
  );
}
